import type { GameCatalogEntry, Sport } from '@shared/types';

interface GameCardProps {
  entry: GameCatalogEntry;
  onPlay: (entry: GameCatalogEntry) => void;
  /** Jugadores conectados ahora en salas de este deporte (dato real del servidor). */
  playing?: number;
}

/**
 * Tarjeta principal de "experiencia" del catálogo (estilo tile de Roblox).
 * Thumbnail cinematográfico + overlay con título. Solo datos reales: los jugadores conectados ahora
 * (de la lista de salas del servidor), y nada si no hay nadie. Sin cifras inventadas (me gusta,
 * visitas) ni clasificaciones de edad que no tenemos.
 */
export const GameCard = ({ entry, onPlay, playing = 0 }: GameCardProps) => {
  return (
    <article
      className="group relative w-72 shrink-0 snap-start overflow-hidden rounded-2xl border border-white/5 bg-ink-800/80 shadow-card transition hover:border-accent-500/50"
    >
      {/* Thumbnail cinematográfico (placeholder; será 3D en Fase 3). */}
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-pitch-gradient">
        {entry.thumbnailUrl ? (
          <img
            src={entry.thumbnailUrl}
            alt={entry.title}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <StadiumPlaceholder sport={entry.sport} />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/30 to-transparent" />
        <SportBadge sport={entry.sport} />
        {playing > 0 && (
          <div className="absolute right-3 top-3 flex flex-col items-end gap-1" data-testid="playing-now">
            <PillStat label="jugando ahora" value={String(playing)} pulse />
          </div>
        )}
      </div>

      {/* Cuerpo */}
      <div className="space-y-2 p-4">
        <h3 className="truncate font-display text-lg font-bold text-white">{entry.title}</h3>
        <p className="line-clamp-2 text-sm text-slate-400">{entry.description}</p>

        <div className="flex flex-wrap gap-1.5">
          {entry.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="pill bg-white/5 text-slate-300">
              {tag}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-end pt-2 text-xs text-slate-400">
          <button onClick={() => onPlay(entry)} className="btn-primary h-8 px-3 text-xs">
            Jugar
          </button>
        </div>
      </div>
    </article>
  );
};

// ───────────── Sub-componentes internos ─────────────

const SportBadge = ({ sport }: { sport: Sport }) => {
  const map: Record<Sport, { label: string; color: string }> = {
    futbol: { label: 'Fútbol', color: 'bg-emerald-500/90 text-emerald-950' },
    beisbol: { label: 'Béisbol', color: 'bg-amber-500/90 text-amber-950' },
    basquetbol: { label: 'Básquetbol', color: 'bg-orange-500/90 text-orange-950' },
  };
  const { label, color } = map[sport];
  return (
    <span className={`pill absolute left-3 top-3 ${color}`}>
      {label}
    </span>
  );
};

const PillStat = ({ label, value, pulse }: { label: string; value: string; pulse?: boolean }) => (
  <span className="pill bg-ink-900/80 text-slate-100 backdrop-blur">
    <span className={`h-1.5 w-1.5 rounded-full bg-neon-green ${pulse ? 'animate-pulse-glow' : ''}`} />
    <span className="font-semibold">{value}</span>
    <span className="text-slate-400">{label}</span>
  </span>
);

const StadiumPlaceholder = ({ sport }: { sport: Sport }) => {
  // Placeholder "cinematográfico" hasta que conectemos el modelo 3D real.
  const symbol: Record<Sport, string> = {
    futbol: '⚽',
    beisbol: '⚾',
    basquetbol: '🏀',
  };
  return (
    <div className="grid h-full w-full place-items-center text-7xl opacity-30">
      <span>{symbol[sport]}</span>
    </div>
  );
};
